import { ApolloError } from "@apollo/client";
import { show } from "@ebay/nice-modal-react";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { client } from "@/client/apollo";
import { __GLOBAL_SESSION_ID } from "@/client/globals";
import { RootState } from "@/data/store";
import {
  SentimentCheckInput,
  SubmitSentimentCheckDocument,
  SubmitSentimentCheckMutation,
  SubmitSentimentCheckMutationVariables,
} from "@/generated/graphql";
import { matchGraphQLErrorCode } from "@/graphql/errors";
import { getCurrentDate } from "@/utils/datetime";

import { SentimentCheckModal } from "./sentiment-check-modal";
import {
  dismissSentimentCheck,
  endSentimentCheck,
  nextSentimentCheck,
} from "./sentiment-check-slice";
import { findCurrentWindow, findNextSchedule } from "./utils";

/**
 * Show the sentiment check modal.
 */
export const showSentimentCheckModal = createAsyncThunk(
  "sentimentCheck/showModal",
  async (payload: { assessmentId: string }, { dispatch, getState }) => {
    const { assessmentId } = payload;

    const sessionId = __GLOBAL_SESSION_ID.current;
    if (!sessionId) {
      throw new Error("Session ID is not set");
    }
    const state = getState() as RootState;
    const { prevSessionId, schedules, dueDate } = state.sentimentCheck;
    if (prevSessionId === sessionId) return;
    // The timer is armed at load; on focus/reconnect the window may have
    // closed since, and the server would reject the check.
    if (findCurrentWindow(schedules, dueDate) === null) {
      dispatch(endSentimentCheck());
      return;
    }
    show(SentimentCheckModal, {
      id: "sentiment-check-modal",
      skipEnabled: state.sentimentCheck.skipEnabled,
      onSubmit: async (input: SentimentCheckInput) => {
        await dispatch(
          submitSentimentCheck({
            assessmentId,
            input,
          })
        );
      },
    });
  }
);

/**
 * Create a sentiment check.
 * After submission, advance to the next scheduled check date.
 */
export const submitSentimentCheck = createAsyncThunk(
  "sentimentCheck/submit",
  async (
    payload: SubmitSentimentCheckMutationVariables,
    { dispatch, getState }
  ) => {
    const { assessmentId, input } = payload;
    let data: SubmitSentimentCheckMutation | null | undefined;
    try {
      ({ data } = await client.mutate<
        SubmitSentimentCheckMutation,
        SubmitSentimentCheckMutationVariables
      >({
        mutation: SubmitSentimentCheckDocument,
        variables: { assessmentId, input },
      }));
    } catch (error) {
      // Only a closed window is final; other failures may succeed on retry.
      if (
        error instanceof ApolloError &&
        matchGraphQLErrorCode(error, ["BAD_REQUEST"])
      ) {
        dispatch(dismissSentimentCheck());
      }
      throw error;
    }

    if (data?.submitSentimentCheck) {
      const state = getState() as RootState;
      const { schedules, dueDate } = state.sentimentCheck;

      // The check just submitted — use current time as the lastCheckDate
      const nextDate = findNextSchedule(
        schedules,
        getCurrentDate().toISOString(),
        dueDate
      );
      if (nextDate) {
        dispatch(nextSentimentCheck({ date: nextDate }));
      } else {
        dispatch(endSentimentCheck());
      }
    }

    return data?.submitSentimentCheck;
  }
);
